import { z } from 'zod';
import { signageConfigSchema } from '@/config/schema';
import type { ResolvedSignageConfig } from '@/config/types';

/**
 * Loads and validates the per-signage config.
 *
 * The legacy app fetched the config before mounting and left every failure as an unhandled
 * rejection — a blank screen on a wall (docs/MIGRATION.md §1, bug 6). This function never
 * throws and never returns a partially valid config: callers get either a fully resolved
 * config or a reason they can read over a remote console.
 */

/** Every way loading can fail, each carrying enough detail to diagnose remotely. */
export type ConfigError =
  | { kind: 'no-signage-id'; pathname: string }
  | { kind: 'http-error'; url: string; status: number; statusText: string; message?: string }
  | { kind: 'invalid-json'; url: string; message: string }
  | {
      kind: 'schema-error';
      url: string;
      message: string;
      issues: z.core.$ZodFlattenedError<unknown>;
    };

export type ConfigResult =
  { ok: true; config: ResolvedSignageConfig } | { ok: false; reason: ConfigError };

/** First path segment of the URL, e.g. `/szeged_2/2` -> `szeged_2`. */
export function getSignageId(pathname: string = window.location.pathname): string {
  return pathname.split('/')[1] ?? '';
}

/**
 * `'/signage_fallback' + pathname` is the legacy fallback and it is what the broker ACL
 * was provisioned against for signages with no explicit `baseTopic`. Byte-for-byte
 * (CLAUDE.md constraint 2).
 */
function fallbackBaseTopic(pathname: string = window.location.pathname): string {
  return '/signage_fallback' + pathname;
}

function fail(reason: ConfigError): { ok: false; reason: ConfigError } {
  // Every failure path logs. A silent failure on an unattended display costs a site visit.
  console.error('[signage] config load failed', reason);
  return { ok: false, reason };
}

export async function loadConfig(
  pathname: string = window.location.pathname,
): Promise<ConfigResult> {
  const signageId = getSignageId(pathname);
  if (!signageId) {
    return fail({ kind: 'no-signage-id', pathname });
  }

  // encodeURIComponent is a deliberate hardening over the legacy string concat: the id
  // comes straight from the URL and is interpolated into a fetch path.
  const url = '/static/' + encodeURIComponent(signageId) + '.json';

  let response: Response;
  try {
    // `Accept: application/json` matters: a history-fallback server (the Vite dev server,
    // and the .htaccess rewrite in production) answers an unknown path with index.html and
    // a 200. Asking for JSON gets a truthful 404 out of the dev server, so a typo'd
    // signage id surfaces as `http-error` rather than as `invalid-json` on stray HTML.
    // Where the server rewrites regardless, `res.json()` still fails and the reason is
    // reported as `invalid-json` — never a blank screen either way.
    response = await fetch(url, { headers: { Accept: 'application/json' } });
  } catch (error) {
    // Network-level failure (display offline, DNS gone). Reported as an http-error with
    // status 0 so operators have one failure shape to look for.
    return fail({
      kind: 'http-error',
      url,
      status: 0,
      statusText: 'fetch failed',
      message: error instanceof Error ? error.message : String(error),
    });
  }

  if (!response.ok) {
    return fail({
      kind: 'http-error',
      url,
      status: response.status,
      statusText: response.statusText,
    });
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch (error) {
    return fail({
      kind: 'invalid-json',
      url,
      message: error instanceof Error ? error.message : String(error),
    });
  }

  const parsed = signageConfigSchema.safeParse(raw);
  if (!parsed.success) {
    return fail({
      kind: 'schema-error',
      url,
      message: z.prettifyError(parsed.error),
      issues: z.flattenError(parsed.error),
    });
  }

  return { ok: true, config: resolveContextualDefaults(parsed.data, pathname) };
}

/**
 * Applies the two defaults that depend on the runtime environment rather than on the
 * config, keeping the schema itself pure and testable under Node.
 */
export function resolveContextualDefaults(
  config: z.infer<typeof signageConfigSchema>,
  pathname: string = window.location.pathname,
): ResolvedSignageConfig {
  const mqttUrl = config.mqttUrl ?? import.meta.env.VITE_MQTT_URL ?? '';
  if (!mqttUrl) {
    console.warn('[signage] no mqttUrl in config and VITE_MQTT_URL is unset');
  }

  return {
    ...config,
    baseTopic: config.baseTopic ?? fallbackBaseTopic(pathname),
    mqttUrl,
  };
}
